import { Vue, Component, Watch } from "@wagich/vue-facing-decorator-metadata";
import { render } from "./site-search.html";

interface SearchHit {
	title: string;
	snippet: string;
	url: string;
	imageUrl: string | null;
	documentType: string | null;
}

interface SearchResult {
	hits: SearchHit[];
	totalFound: number;
	page: number;
	hasMore: boolean;
}

const placeholderRotationInterval = 1900;
@Component({
	render,
})
export default class SiteSearch extends Vue {
	query: string = "";
	isOpen: boolean = false;
	isLoading: boolean = false;
	hits: SearchHit[] = [];
	page: number = 1;
	hasMore: boolean = false;
	totalFound: number = 0;
	activeIndex: number = -1;

	// Decorative, animated placeholder words cycled while the (empty) search box is open.
	// The static, accessible hint lives in the input's native `placeholder` attribute.
	placeholderWords: string[] = ["Filmen", "Specials", "Essen", "Tickets", "Gutscheinen", "Events", "Popcorn"];
	placeholderIndex: number = 0;
	placeholderVisible: boolean = true;

	private debounceTimer: ReturnType<typeof setTimeout> | null = null;
	private placeholderTimer: ReturnType<typeof setInterval> | null = null;

	get hasResults(): boolean {
		return this.hits.length > 0;
	}

	get currentPlaceholderWord(): string {
		return this.placeholderWords[this.placeholderIndex];
	}

	get showNoResults(): boolean {
		return !this.isLoading && this.query.length >= 2 && !this.hasResults;
	}

	get activeDescendantId(): string | undefined {
		if (this.activeIndex >= 0 && this.activeIndex < this.hits.length) {
			return `site-search-hit-${this.activeIndex}`;
		}
		return undefined;
	}

	open() {
		this.isOpen = true;
		this.startPlaceholderRotation();
		this.$nextTick(() => {
			const input = (this.$el as HTMLElement).querySelector<HTMLInputElement>(".site-search__input");
			input?.focus();
		});
	}

	close() {
		this.isOpen = false;
		this.query = "";
		this.hits = [];
		this.page = 1;
		this.hasMore = false;
		this.totalFound = 0;
		this.activeIndex = -1;
		this.stopPlaceholderRotation();
	}

	beforeUnmount() {
		this.stopPlaceholderRotation();
		if (this.debounceTimer) {
			clearTimeout(this.debounceTimer);
		}
	}

	private startPlaceholderRotation() {
		if (this.placeholderTimer != null) {
			return;
		}
		// Respect users who prefer reduced motion: leave the static native placeholder in place.
		if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
			return;
		}

		this.placeholderTimer = setInterval(() => {
			this.placeholderVisible = false;
			// After the fade-out transition completes, swap the word and fade back in.
			setTimeout(() => {
				if (this.placeholderTimer == null) {
					return;
				}
				this.placeholderIndex = (this.placeholderIndex + 1) % this.placeholderWords.length;
				this.placeholderVisible = true;
			}, 350);
		}, placeholderRotationInterval);
	}

	private stopPlaceholderRotation() {
		if (this.placeholderTimer != null) {
			clearInterval(this.placeholderTimer);
			this.placeholderTimer = null;
		}
		this.placeholderVisible = true;
		this.placeholderIndex = 0;
	}

	@Watch("query")
	onQueryChanged() {
		if (this.debounceTimer) {
			clearTimeout(this.debounceTimer);
		}

		if (this.query.length < 2) {
			this.hits = [];
			this.page = 1;
			this.hasMore = false;
			this.totalFound = 0;
			this.activeIndex = -1;
			return;
		}

		this.debounceTimer = setTimeout(() => {
			this.page = 1;
			this.hits = [];
			this.activeIndex = -1;
			this.search();
		}, 200);
	}

	async search() {
		this.isLoading = true;
		try {
			const params = new URLSearchParams({
				q: this.query,
				page: this.page.toString(),
				perPage: "8",
			});
			const response = await fetch(`/api/search?${params}`);
			if (!response.ok) {
				return;
			}
			const data: SearchResult = await response.json();

			if (this.page === 1) {
				this.hits = data.hits;
			} else {
				this.hits = [...this.hits, ...data.hits];
			}
			this.hasMore = data.hasMore;
			this.totalFound = data.totalFound;
		} finally {
			this.isLoading = false;
		}
	}

	loadMore() {
		this.page++;
		this.search();
	}

	onKeydown(event: KeyboardEvent) {
		if (event.key === "Escape") {
			this.close();
		}
	}

	onInputKeydown(event: KeyboardEvent) {
		switch (event.key) {
			case "Escape":
				this.close();
				break;
			case "ArrowDown":
				event.preventDefault();
				if (this.hits.length > 0) {
					this.activeIndex = Math.min(this.activeIndex + 1, this.hits.length - 1);
					this.scrollActiveIntoView();
				}
				break;
			case "ArrowUp":
				event.preventDefault();
				if (this.activeIndex > 0) {
					this.activeIndex--;
					this.scrollActiveIntoView();
				} else {
					this.activeIndex = -1;
				}
				break;
			case "Home":
				if (this.hits.length > 0) {
					event.preventDefault();
					this.activeIndex = 0;
					this.scrollActiveIntoView();
				}
				break;
			case "End":
				if (this.hits.length > 0) {
					event.preventDefault();
					this.activeIndex = this.hits.length - 1;
					this.scrollActiveIntoView();
				}
				break;
			case "Enter":
				if (this.activeIndex >= 0 && this.activeIndex < this.hits.length) {
					event.preventDefault();
					window.location.href = this.hits[this.activeIndex].url;
				}
				break;
		}
	}

	private scrollActiveIntoView() {
		this.$nextTick(() => {
			const active = (this.$el as HTMLElement).querySelector<HTMLElement>(`#site-search-hit-${this.activeIndex}`);
			active?.scrollIntoView({ block: "nearest" });
		});
	}

	onOverlayClick(event: MouseEvent) {
		if ((event.target as HTMLElement).classList.contains("site-search__overlay")) {
			this.close();
		}
	}
}
